'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useTableFilters } from '../useTableFilters';
import type { ReactNode } from 'react';
import type { BaseTableFilterProps } from './types';

/**
 * Args provided to a custom filter's `render` callback.
 *
 * The custom variant is an escape hatch for filters whose UI doesn't fit any
 * existing variant. URL state is still managed by the store; consumers provide
 * the UI only and serialize their value into the raw URL string themselves
 * (same pattern as every built-in variant).
 */
export interface CustomFilterRenderProps {
  /** Current URL value for this search param, or null if not set. */
  value: string | null;
  /** Write a new value. Passing null (or empty string) clears the param. */
  setValue: (value: string | null) => void;
  /** Remove the param from the URL. Shorthand for `setValue(null)`. */
  clear: () => void;
}
export interface CustomTableFilterProps extends BaseTableFilterProps {
  searchParam: string;
  variant: 'custom';
  render: (props: CustomFilterRenderProps) => ReactNode;
  /**
   * Optional allow-list of valid string values for this filter. When provided:
   * - `sanitizeSearchParams` drops any URL value not in the list
   * - `InferSearchParams` narrows `filterParams[key]` to the literal union
   *
   * Use `as const` (or a readonly tuple) to preserve literal types:
   * `validValues: ['Developer', 'Customer'] as const`.
   */
  validValues?: readonly string[];

  // Explicit `never` on variant-discriminating fields — mirrors other variants.
  filterOptions?: never;
  groups?: never;
  placeholder?: never;
  value?: never;
  defaultValue?: never;
  fromDateParam?: never;
  toDateParam?: never;
}
export function CustomTableFilter(t0) {
  const $ = _c(13);
  const {
    searchParam,
    searchParamsToClearOnChange,
    render,
    filterType: t1
  } = t0;
  const filterType = t1 === undefined ? "rsc" : t1;
  const searchParams = useSearchParams();
  const {
    updateFilterValue
  } = useTableFilters(filterType);
  let t2;
  if ($[0] !== render || $[1] !== searchParam || $[2] !== searchParams || $[3] !== searchParamsToClearOnChange || $[4] !== updateFilterValue) {
    const value = searchParams?.get(searchParam) ?? null;
    let t3;
    let t4;
    if ($[6] !== searchParam || $[7] !== searchParamsToClearOnChange || $[8] !== updateFilterValue) {
      t3 = next => updateFilterValue(searchParam, next, searchParamsToClearOnChange);
      t4 = () => updateFilterValue(searchParam, null, searchParamsToClearOnChange);
      $[6] = searchParam;
      $[7] = searchParamsToClearOnChange;
      $[8] = updateFilterValue;
      $[9] = t3;
      $[10] = t4;
    } else {
      t3 = $[9];
      t4 = $[10];
    }
    t2 = render({
      value,
      setValue: t3,
      clear: t4
    });
    $[0] = render;
    $[1] = searchParam;
    $[2] = searchParams;
    $[3] = searchParamsToClearOnChange;
    $[4] = updateFilterValue;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <>{t2}</>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}